import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/v2/components/ui/button";
import { Chip } from "@/v2/components/ui/chip";
import HttpService from "../../hooks/Https-services";
import { APIRoutes } from "../../utils/constants";

// HTPTV-636: the buyer's "gifts you've sent" panel (status + resend).
const STATUS: Record<string, { label: string; color: string }> = {
  purchased: { label: "Processing", color: "var(--v2-warning)" },
  scheduled: { label: "Scheduled", color: "var(--v2-action)" },
  delivered: { label: "Delivered", color: "var(--v2-action)" },
  redeemed: { label: "Redeemed", color: "var(--v2-success)" },
  converted: { label: "Subscribed", color: "var(--v2-success)" },
  expired: { label: "Expired", color: "var(--v2-muted-foreground)" },
};

const tierLabel = (t: string) =>
  t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
const termText = (b: string, n: number) =>
  `${n} ${b === "annual" ? "year" : "month"}${n > 1 ? "s" : ""}`;

const SentGifts = () => {
  const [gifts, setGifts] = useState<any[]>([]);
  const [resending, setResending] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await HttpService.get(APIRoutes.GiftSubscriptionSent);
        if (Array.isArray(res?.data?.data)) setGifts(res.data.data);
      } catch (e) {
        // no sent gifts / not available
      }
    })();
  }, []);

  if (!gifts.length) return null;

  const handleResend = async (id: number) => {
    setResending(id);
    try {
      await HttpService.post(
        APIRoutes.ResendGiftSubscription.replace(":id", String(id)),
        {},
      );
      toast.success("Gift email resent");
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Could not resend");
    } finally {
      setResending(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "divider",
        mt: 3,
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={700}
        color="text.secondary"
        letterSpacing={1.2}
        mb={2}
        textTransform="none"
      >
        Gifts you've sent
      </Typography>
      <Stack divider={<Divider />}>
        {gifts.map((g) => {
          const st = STATUS[g.state] || {
            label: g.state,
            color: "var(--v2-muted-foreground)",
          };
          return (
            <Stack
              key={g.id}
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
              spacing={1}
              py={1.5}
            >
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  textTransform="none"
                >
                  {g.recipientName || g.recipientEmail}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textTransform="none"
                >
                  {tierLabel(g.planTier)} ·{" "}
                  {termText(g.billingModel, g.termCount)}
                  {g.recipientName ? ` · ${g.recipientEmail}` : ""}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Chip
                  color={st.color || "var(--v2-muted-foreground)"}
                  size="sm"
                >
                  {st.label}
                </Chip>
                {g.state === "delivered" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={resending === g.id}
                    onClick={() => handleResend(g.id)}
                    className="text-action rounded-full px-3 text-[13px] hover:bg-action/10 hover:text-action"
                  >
                    {resending === g.id ? "Resending…" : "Resend"}
                  </Button>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default SentGifts;
